<template>
    <div class="card card--sm movable" @click="$emit('cardClicked')" :class="{draggable: draggable, 'card--bordered': unapplied}">
        <header class="card__header">
            <div class="content-tracker-board__image">
                <div class="image-block image-block--sm">
                    <img :src="LogoHelper.getLogo(startup.logoUrl)" alt=""/>
                </div>

                <div class="dropdown dropdown--quaternary" @mouseover="showDropDownMenu = true" @mouseleave="showDropDownMenu = false">
                    <span class="badge dropdown-toggle" :class="{'badge__active': startup.has_account, 'badge__inactive': !startup.has_account}"></span>
                    
                    <div class="dropdown-menu" :class="{'show': showDropDownMenu}">
                        <div class="dropdown-menu__inner">
                            <template v-if="startup.has_account">
                                <p>This startup has an account on SwitchPitch</p>
                            </template>

                            <template v-else>
                                <p>This startup does not have an account on SwitchPitch</p>
                                <a v-if="startup.id" @click.stop="$emit('invite', startup.id)" class="button button--bordered">Invite</a>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mr-auto">
                <h5 class="heading5 d-flex text-break pr-3">
                    <a @click.stop v-if="startup.id" :href="'/startups/' + startup.id" target="_blank">{{ startup.name }}</a>
                    <template v-else>{{ startup.name }}</template>
                </h5>
                <template v-if="startup.irl_score">
                    <p class="mb-0">IRL Score: {{startup.irl_score}}</p>
                </template>
            </div>

            <div
                v-if="!unapplied"
                :class="'card__header__pin card__header__pin' + (startup.pinned ? '--active' : '')"
                @click.stop="$emit('togglePinned')"
            />
        </header>

        <template v-if="typeof startup.added_by_ecosystem !== 'undefined'">
            <div class="card__reffered">Referred by <span @click.stop v-html="startup.added_by_ecosystem.link"></span></div>

            <template v-if="typeof startup.added_by_ecosystem.note !== 'undefined'">
                <div
                    v-if="startup.added_by_ecosystem.note"
                    class="card__reffered link"
                    @click.stop="$bvModal.show(`note-modal-${startup.id}`)"
                >
                    Note from <span v-html="startup.added_by_ecosystem.user_name"></span>
                </div>

                <b-modal v-bind:id="`note-modal-${startup.id}`" hide-footer modal-class="note-modal modal-w-lg">
                    <div class="modal-header">
                        <div class="image-block image-block--mds">
                            <a target="_blank" :href="'/startups/' + startup.id"><img :src="LogoHelper.getLogo(startup.logoUrl)" :alt="startup.name"/></a>
                        </div>
                        <h3 class="heading3 heading3--secondary mt-3"><a class="mt-2" target="_blank" :href="'/startups/' + startup.id">{{ startup.name }}</a></h3>
                    </div>

                    <div class="modal-content">
                        <header class="modal-content__header">
                            <div class="image-block image-block--rounded image-block--rounded--mds">
                                <img :src="LogoHelper.getLogo(startup.added_by_ecosystem.user_avatar)" :alt="startup.added_by_ecosystem.user_name" />
                            </div>
                            <div>
                                <p>Note by {{ startup.added_by_ecosystem.user_name }} from <span v-html="startup.added_by_ecosystem.link"></span></p>
                                <a v-if="startup.added_by_ecosystem.user_linkedin" :href="startup.added_by_ecosystem.user_linkedin" target="_blank" class="icon-in"></a>
                            </div>
                        </header>
                        <div v-html="startup.added_by_ecosystem.note"/>
                    </div>
                </b-modal>
            </template>
        </template>

        <p v-if="startup.id">
            <VueClamp
                autoresize
                :max-lines="3"
            >
                {{ startup.description }}
            </VueClamp>

            <template v-if="startup.description?.length">
                (<a @click.stop="getStartupDetails" class="link-click__text" :id="'details-link-'+startup.id">more</a>)
            </template>
        </p>

        <p v-else>Account not activated</p>

        <p class="card__counter">{{startup.dates}}</p>

        <a v-if="!unapplied && !!startup.questions_count" @click.stop="$emit('showAnswers')" class="card__answers">
            View Answers
            <time class="card__answers__date">{{ dateHelper.decodeDate(startup.apply_date) }}</time>
        </a>

        <footer v-if="hasReviews" class="card__footer">
            <div v-if="startup.overall_score || startup.interest_level" @click.stop="function () { if (addData.userPermissions.indexOf('tracker-review-list') >= 0) $emit('showReviews')}">
                <div v-if="startup.overall_score">
                    <b>Score: {{startup.overall_score}} / {{startup.max_overall_score}}</b>
                    <span class="link ml-2" v-if="startup.reviews_count">{{startup.reviews_count}}</span>
                </div>

                <div v-else-if="startup.interest_level" class="rating-section">
                    <div class="rating">
                        <span v-for="index in [1,2,3,4,5]" :key="index" :class="{'rating__current': startup.interest_level >= index}"></span>
                    </div>
                    <span class="rating__result" v-if="startup.reviews_count">{{startup.reviews_count}}</span>
                </div>
            </div>

            <div v-else class="no-rating-section">
                No ratings
            </div>

            <a v-if="startup.user_has_review && addData.userPermissions.indexOf('tracker-review-own-edit') >= 0" class="link" @click.stop="$emit('editReview')">Edit</a>
            <a v-if="!startup.user_has_review && addData.userPermissions.indexOf('tracker-review-add') >= 0" class="link" @click.stop="$emit('editReview')">+ Add</a>
        </footer>

        <footer v-if="unapplied" class="card__footer">
            <p class="card__counter">{{startup.time_ago}}</p>
            <a v-if="startup.id" @click="$emit('sendMessage')" class="link link--message">Message</a>
        </footer>

        <b-popover ref="details" :target="'details-link-'+startup.id">
            <div class="popover__content" v-click-outside="closeDetailsPopover">
                <header class="popover__content__header">
                    <div class="popover__content__side">
                        <img class="main-table__logo" :src="LogoHelper.getLogo(startup.logoUrl)" alt="">
                    </div>
                    <div>
                        <h4 class="heading4"><a :href="'/startups/' + startup.id">{{ startup.name }}</a></h4>
                        <p>{{startup.description}}</p>
                    </div>
                </header>

                <div class="popover__content__body">
                    <div class="popover__content__side">
                        <h6 class="heading6">Trackers:</h6>
                    </div>
                    <div>
                        <p>
                            <span v-for="(tracker, index) in startupDetails.trackers" :key="tracker.id">
                                <a :href="'/trackers/' + tracker.id" target="_blank">{{ tracker.name }}</a>
                                <template v-if="index < startupDetails.trackers.length - 1">, </template>
                            </span>
                        </p>
                    </div>
                </div>

                <div class="popover__content__body">
                    <div class="popover__content__side">
                        <h6 class="heading6">Tags:</h6>
                    </div>

                    <div class="tags-list">
                        <span v-for="tag in startupDetails.tags" :key="tag.id" :id="'tag-' + startup.id + '-' + tag.id" class="tags-list__tag" :title="tag.name">
                            {{ tag.name }}
                            <span @click="removeTag(tag.id)" class="tags-list__tag__close"></span>
                            <b-tooltip :target="'tag-' + startup.id + '-' + tag.id" placement="right" variant="light" :title="tag.description"/>
                        </span>

                        <a v-if="!showAddTags" @click.stop="showAddTags = true" class="tags-list__add">+ Add</a>

                        <multiselect v-if="showAddTags"
                                     v-model="addedTags"
                                     :multiple="true"
                                     :options="addData.tagsList"
                                     select-label=""
                                     deselect-label=""
                                     label="name"
                                     track-by="name"
                                     class="multiselect-mid pl-2"
                        >
                        </multiselect>

                        <template v-if="showAddTags">
                            <a v-if="addedTags.length" @click="addTags" class="tags-list__add">+ Add Tags</a>
                            <a @click="showAddTags = false">Cancel</a>
                        </template>
                    </div>
                </div>
            </div>
        </b-popover>
    </div>
</template>

<script>

import startups from "../../services/startups.service";
import multiselect from 'vue-multiselect'
import tags from '../../services/tags.service'
import VueClamp from "vue-clamp";

export default {
    props: {
        startup: Object,
        addData: Object,
        draggable: {
            type: Boolean,
            default: false,
        },
        hasReviews: {
            type: Boolean,
            default: false,
        },
        unapplied: {
            type: Boolean,
            default: false,
        }
    },
    components: {
        multiselect,
        VueClamp,
    },
    data() {
        return {
            startupDetails: {
                description: '',
                tags: [],
                trackers: [],
            },

            addedTags: [],
            addedTracker: null,

            showAddTags: false,
            showDropDownMenu: false,
        }
    },

    methods: {
        getStartupDetails() {
            startups.getDetails(this.startup.id).then(response => {
                this.startupDetails = response.data
                this.trackersCount = this.startupDetails.trackers.length
            })
        },

        closeDetailsPopover() {
            this.cancelAddTags()
            this.$refs.details.$emit('close')
        },

        cancelAddTags() {
            this.showAddTags = false
            this.addedTags = []
        },

        addTags() {
            let addedTags = this.arrayHelper.arrayColumn(this.addedTags, 'id')
            let startups = [this.startup.id]
            tags.addTags(startups, addedTags).then(() => {
                this.cancelAddTags()
                this.getStartupDetails()
            })
        },

        removeTag(tagId) {
            let removedTags = [tagId]
            let startups = [this.startup.id]
            tags.removeTags(startups, removedTags).then(() => {
                this.getStartupDetails()
            })
        },
    }
}
</script>

<style scoped>

</style>
